<script lang="ts">
  import { onMount } from 'svelte';
  import { Link } from 'svelte-routing';
  import { api } from '../../lib/api/client';
  import type { Group, CrashSender, CrashSettings, Member, Person } from '../../lib/api/types';
  import { currentUser, managedBy } from '../../lib/stores/auth';
  import PageHeader from '../../lib/components/PageHeader.svelte';
  import { t } from '../../lib/i18n/index.ts';

  let enabled = $state(false);
  let chosen = $state(new Set<string>());
  let people: Person[] = $state([]);
  let senders: CrashSender[] = $state([]);
  let phone = $state('');
  let error = $state('');
  let saved = $state(false);
  // drivingReport is the driving report switch (spec 004 FR-054).
  let drivingReport = $state(false);
  let drivingError = $state('');
  // sosChosen is the SOS list (spec 004 FR-012), apart from crash recipients.
  let sosChosen = $state(new Set<string>());
  let sosError = $state('');
  let sosSaved = $state(false);

  onMount(async () => {
    try {
      const s = await api.get<CrashSettings>('/me/crash-settings');
      enabled = s.enabled;
      chosen = new Set(s.recipients.map((r) => r.user_id));
      const groups = await api.get<Group[]>('/groups');
      const all = new Map<string, Person>();
      for (const c of groups) {
        for (const m of await api.get<Member[]>(`/groups/${c.id}/members`)) {
          if (m.user_id !== $currentUser?.id) all.set(m.user_id, { user_id: m.user_id, display_name: m.display_name });
        }
      }
      people = [...all.values()].sort((a, b) => a.display_name.localeCompare(b.display_name));
      senders = await api.get<CrashSender[]>('/me/crash-senders');
      sosChosen = new Set((await api.get<Person[]>('/me/sos-contacts')).map((p) => p.user_id));
      const me = await api.get<{ phone?: string; driving_report?: boolean }>('/auth/me');
      phone = me.phone ?? '';
      drivingReport = !!me.driving_report;
    } catch (e: any) {
      error = e.message;
    }
  });

  function toggle(id: string, on: boolean) {
    const next = new Set(chosen);
    if (on) next.add(id);
    else next.delete(id);
    chosen = next;
    save();
  }

  // save sends crash detection and its recipients as soon as either changes (010 FR-080).
  async function save() {
    saved = false;
    error = '';
    try {
      await api.put('/me/crash-settings', { enabled, recipients: [...chosen] });
      saved = true;
    } catch (e: any) {
      error = e.message;
    }
  }

  function toggleSos(id: string, on: boolean) {
    const next = new Set(sosChosen);
    if (on) next.add(id);
    else next.delete(id);
    sosChosen = next;
    saveSos();
  }

  // saveSos sends the SOS list as soon as a person is ticked or unticked (010 FR-080).
  async function saveSos() {
    sosSaved = false;
    sosError = '';
    try {
      await api.put('/me/sos-contacts', { recipients: [...sosChosen] });
      sosSaved = true;
    } catch (e: any) {
      sosError = e.message;
    }
  }

  // around splits a message at the spot its account link fills.
  const around = (message: string) => message.split('\u0000');
  const addPhone = around(t('settingsCrashAddPhone', { account: '\u0000' }));

  async function saveDriving() {
    drivingError = '';
    try {
      drivingReport = !!(await api.patch<{ driving_report?: boolean }>('/me', { driving_report: drivingReport })).driving_report;
    } catch (e: any) {
      drivingError = e.message;
      drivingReport = !drivingReport;
    }
  }
</script>

{#if $managedBy}
  <p class="text-sm mb-4"><i aria-hidden="true" class="las la-lock mr-1"></i>{t('settingsSafetyManaged', { parent: $managedBy })}</p>
{/if}
<!-- A child's settings are their parents' to change (spec 001 FR-098). -->
<fieldset disabled={!!$managedBy} class="min-w-0">
<div class="card p-5 mb-6 space-y-4">
  <PageHeader title={t('settingsSosTitle')} level="section" />
  <p class="text-sm text-text-muted">
    {t('settingsSosAbout')}
  </p>
  <div>
    <p class="text-sm font-semibold mb-1">{t('settingsSosSendTo')}</p>
    {#each people as p (p.user_id)}
      <label class="flex items-center gap-2 text-sm py-0.5">
        <input type="checkbox" checked={sosChosen.has(p.user_id)} onchange={(e) => toggleSos(p.user_id, e.currentTarget.checked)} />
        {p.display_name}
      </label>
    {:else}
      <p class="text-sm text-text-muted">{t('settingsNobodyInGroups')}</p>
    {/each}
  </div>
  {#if sosSaved}<p class="text-sm" style="color: var(--theme-success);" aria-live="polite">{t('settingsSaved')}</p>{/if}
  {#if sosError}<p class="text-sm" style="color: var(--theme-error);">{sosError}</p>{/if}
</div>

<div class="card p-5 mb-6 space-y-4">
  <PageHeader title={t('settingsCrashTitle')} level="section" />
  <p class="text-sm text-text-muted">
    {t('settingsCrashAbout')}
  </p>
  <label class="flex items-center gap-2">
    <input type="checkbox" bind:checked={enabled} onchange={save} />
    {t('settingsCrashDetect')}
  </label>
  {#if enabled}
    <div>
      <p class="text-sm font-semibold mb-1">{t('settingsCrashSendTo')}</p>
      {#each people as p (p.user_id)}
        <label class="flex items-center gap-2 text-sm py-0.5">
          <input type="checkbox" checked={chosen.has(p.user_id)} onchange={(e) => toggle(p.user_id, e.currentTarget.checked)} />
          {p.display_name}
        </label>
      {:else}
        <p class="text-sm text-text-muted">{t('settingsNobodyInGroups')}</p>
      {/each}
    </div>
    <!-- Recipients call the account's number (spec 001 FR-007, 004 FR-007). -->
    <p class="text-sm text-text-muted">
      {#if phone}
        {@const callAt = around(t('settingsCrashCallAt', { phone, account: '\u0000' }))}
        {callAt[0]}<Link to="/settings/profile">{t('settingsAccountLink')}</Link>{callAt[1]}
      {:else}
        {addPhone[0]}<Link to="/settings/profile">{t('settingsAccountLink')}</Link>{addPhone[1]}
      {/if}
    </p>
  {/if}
  {#if saved}<p class="text-sm" style="color: var(--theme-success);" aria-live="polite">{t('settingsSaved')}</p>{/if}
  {#if error}<p class="text-sm" style="color: var(--theme-error);">{error}</p>{/if}
</div>

<div class="card p-5 mb-6">
  <PageHeader title={t('settingsDrivingTitle')} level="section" />
  <label class="flex items-start gap-3">
    <input type="checkbox" class="mt-1" bind:checked={drivingReport} onchange={saveDriving} />
    <span>
      <span class="block">{t('settingsDrivingNote')}</span>
      <span class="block text-xs text-text-muted">
        {t('settingsDrivingAbout')}
      </span>
    </span>
  </label>
  {#if drivingError}<p class="text-sm mt-2" style="color: var(--theme-error);">{drivingError}</p>{/if}
</div>
</fieldset>

{#if senders.length > 0}
  <div class="card p-5">
    <PageHeader title={t('settingsCrashSenders')} level="section" />
    <ul class="text-sm space-y-1">
      {#each senders as s (s.user_id)}
        <li>{s.enabled ? s.display_name : t('settingsCrashSenderOff', { member: s.display_name })}</li>
      {/each}
    </ul>
  </div>
{/if}
